// Calendar-day helpers for the day-scoped HR surfaces (Overview > Pulse /
// By site). A "day" here is always a bare YYYY-MM-DD string — the same
// shape GET /api/hr/overview takes as ?date= and echoes back — never a
// Date, so nothing downstream can accidentally reintroduce a time.
//
// APP_TZ mirrors the server's APP_TIMEZONE (see hr_overview.go: the work day
// rolls over at local midnight there, not the browser's). Hardcoded to match
// the same constant already inlined in BoardPage/PersonPage; if
// APP_TIMEZONE ever becomes configurable, this is the one place to plumb it
// through.
export const APP_TZ = 'Asia/Kolkata';

/** Today's calendar date in APP_TZ. 'en-CA' is the shortest route to a real
    YYYY-MM-DD out of Intl. */
export function todayISO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: APP_TZ });
}

/** Steps a YYYY-MM-DD by whole days. Done in UTC precisely because these are
    calendar dates and not instants — going via local time would drop or
    repeat a day across a DST boundary. */
export function shiftISO(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Inclusive calendar report bounds, never a rolling window or browser-local instant. */
export function reportDateRange(period: 'weekly' | 'monthly', iso: string): { from: string; to: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const date = new Date(`${iso}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== iso) return null;
  if (period === 'monthly') {
    const from = `${iso.slice(0, 7)}-01`;
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + 1);
    date.setUTCDate(0);
    return { from, to: date.toISOString().slice(0, 10) };
  }
  const from = shiftISO(iso, -((date.getUTCDay() + 6) % 7));
  return { from, to: shiftISO(from, 6) };
}

/** "Wed, 13 Aug" — enough to orient without the year's noise. Formatted in
    UTC so the label always names the date it was handed. */
export function fmtDayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}
